import { useRef, useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";

import MainCard from "components/MainCard";
import IconButton from "components/@extended/IconButton";
import Transitions from "components/@extended/Transitions";

import BellOutlined from "@ant-design/icons/BellOutlined";
import CheckCircleOutlined from "@ant-design/icons/CheckCircleOutlined";
import ArrowRightOutlined from "@ant-design/icons/ArrowRightOutlined";
import DollarOutlined from "@ant-design/icons/DollarOutlined";
import ClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";
import ExclamationCircleOutlined from "@ant-design/icons/ExclamationCircleOutlined";
import PushpinOutlined from "@ant-design/icons/PushpinOutlined";
import ShoppingOutlined from "@ant-design/icons/ShoppingOutlined";

import {
  Fetch_Notifications,
  Mark_Notification_Read,
  Mark_All_Read,
} from "../../../../Redux/Action/NotificationAction/NotificationAction";

// ─── Urgency config map ───────────────────────────────────────────────────────
const URGENCY = {
  critical: { border: "#ff4d4f", bg: "#fff1f0", badge: "error", chip: "#ff4d4f", text: "#cf1322" },
  high: { border: "#fa8c16", bg: "#fff7e6", badge: "warning", chip: "#fa8c16", text: "#ad4e00" },
  medium: { border: "#1890ff", bg: "#e6f4ff", badge: "info", chip: "#1890ff", text: "#0958d9" },
  low: { border: "#8c8c8c", bg: "#fafafa", badge: "default", chip: "#8c8c8c", text: "#595959" },
};

// ─── Days-until label helper ──────────────────────────────────────────────────
function getDueLabel(daysUntilDue) {
  const d = parseInt(daysUntilDue ?? 999);
  if (d < 0) return { label: `Overdue ${Math.abs(d)}d`, color: "#ff4d4f" };
  if (d === 0) return { label: "Due Today", color: "#ff4d4f" };
  if (d <= 2) return { label: `Due in ${d}d`, color: "#fa8c16" };
  if (d <= 5) return { label: `Due in ${d}d`, color: "#1890ff" };
  return { label: `In ${d}d`, color: "#8c8c8c" };
}

// ─── Single notification row ──────────────────────────────────────────────────
function NotifRow({ notif, onRead, onNavigate }) {
  const urgency = notif.urgency || "medium";
  const cfg = URGENCY[urgency] || URGENCY.medium;
  const isPayment = notif.notification_type === "payment_schedule";
  const isPayable = notif.direction === "payable";
  const dueInfo = isPayment ? getDueLabel(notif.days_until_due) : null;
  const isUnread = !notif.is_read;

  const handleClick = () => {
    if (isUnread) onRead(notif.id);
    if (notif.action_url) onNavigate(notif.action_url);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        px: 2,
        py: 1.5,
        gap: 1.5,
        cursor: notif.action_url ? "pointer" : "default",
        bgcolor: isUnread ? cfg.bg : "transparent",
        borderLeft: `3px solid ${isUnread ? cfg.border : "transparent"}`,
        transition: "background 0.15s",
        "&:hover": { bgcolor: isUnread ? cfg.bg : "#f5f5f5" },
        position: "relative",
      }}
    >
      {/* Avatar / icon */}
      <Avatar
        sx={{
          width: 36,
          height: 36,
          bgcolor: `${cfg.border}18`,
          color: cfg.border,
          flexShrink: 0,
          mt: "2px",
        }}
      >
        {isPayment ? (
          isPayable ? <DollarOutlined style={{ fontSize: 16 }} /> : <ArrowRightOutlined style={{ fontSize: 16 }} />
        ) : notif.notification_type === "stock_alert" ? (
          <ExclamationCircleOutlined style={{ fontSize: 16 }} />
        ) : (
          <ClockCircleOutlined style={{ fontSize: 16 }} />
        )}
      </Avatar>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" mb={0.3}>
          {/* Pin icon for pinned */}
          {notif.is_pinned === 1 && (
            <PushpinOutlined style={{ fontSize: 11, color: cfg.text }} />
          )}

          {/* Title */}
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: isUnread ? 600 : 500,
              color: "#1a1a1a",
              lineHeight: 1.3,
              flex: 1,
            }}
          >
            {notif.title}
          </Typography>
        </Stack>

        {/* Message */}
        <Typography sx={{ fontSize: 12, color: "#595959", lineHeight: 1.5, mb: 0.5 }}>
          {notif.message}
        </Typography>

        {/* Chips row */}
        <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
          {/* Payment direction badge */}
          {isPayment && (
            <Chip
              label={isPayable ? "PAYABLE" : "RECEIVABLE"}
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 700,
                bgcolor: isPayable ? "#fff1f0" : "#f6ffed",
                color: isPayable ? "#cf1322" : "#237804",
                border: `1px solid ${isPayable ? "#ffa39e" : "#b7eb8f"}`,
                "& .MuiChip-label": { px: 0.8, letterSpacing: "0.5px" },
              }}
            />
          )}

          {/* Due countdown */}
          {dueInfo && (
            <Chip
              icon={<ClockCircleOutlined style={{ fontSize: 9, color: dueInfo.color }} />}
              label={dueInfo.label}
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 600,
                color: dueInfo.color,
                bgcolor: `${dueInfo.color}12`,
                border: `1px solid ${dueInfo.color}40`,
                "& .MuiChip-label": { px: 0.8 },
                "& .MuiChip-icon": { ml: 0.5 },
              }}
            />
          )}

          {/* Reference type */}
          {notif.reference_type && (
            <Chip
              icon={<ShoppingOutlined style={{ fontSize: 9, color: "#8c8c8c" }} />}
              label={notif.reference_type === "purchase_order" ? "Purchase" : notif.reference_type === "petty_cash" ? "Payment" : "Sale"}
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                color: "#595959",
                bgcolor: "#f5f5f5",
                border: "1px solid #d9d9d9",
                "& .MuiChip-label": { px: 0.8 },
                "& .MuiChip-icon": { ml: 0.5 },
              }}
            />
          )}
        </Stack>
      </Box>

      {/* Unread dot */}
      {isUnread && (
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: cfg.border,
            mt: "6px",
            flexShrink: 0,
          }}
        />
      )}
    </Box>
  );
}

// ─── Section header (Pinned / Earlier) ───────────────────────────────────────
function SectionLabel({ label, count }) {
  return (
    <Box
      sx={{
        px: 2,
        py: 0.6,
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        bgcolor: "#fafafa",
        borderBottom: "1px solid #f0f0f0",
      }}
    >
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#8c8c8c", letterSpacing: "0.8px", textTransform: "uppercase" }}>
        {label}
      </Typography>
      {count > 0 && (
        <Box sx={{ bgcolor: "#f0f0f0", borderRadius: 10, px: 0.8, py: 0.1 }}>
          <Typography sx={{ fontSize: 10, color: "#595959", fontWeight: 600 }}>{count}</Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Main Notification Component ─────────────────────────────────────────────
export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const anchorRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { notifications = [], unread_count = 0, loading = false } =
    useSelector((state) => state.notification || {});

  
    const { user } = useSelector((state) => state.auth || {});
  const companyId = user?.user?.company_id;

  // ── Fetch on mount + poll every 15s (near-instant admin alerts, e.g.
  // cash-shortage on shift close) ────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    dispatch(Fetch_Notifications(false, companyId));
    const interval = setInterval(() => dispatch(Fetch_Notifications(false, companyId)), 15 * 1000);
    return () => clearInterval(interval);
  }, [dispatch, companyId]);

  // ── Re-fetch when panel opens ─────────────────────────────────────────────
  const handleToggle = useCallback(() => {
    setOpen((prev) => {
      if (!prev) dispatch(Fetch_Notifications(false, companyId));
      return !prev;
    });
  }, [dispatch, companyId]);

  const handleClose = useCallback((e) => {
    if (anchorRef.current?.contains(e.target)) return;
    setOpen(false);
  }, []);

  const handleRead = useCallback((id) => dispatch(Mark_Notification_Read(id)), [dispatch]);
  const handleAllRead = useCallback(() => dispatch(Mark_All_Read()), [dispatch]);
  const handleNavigate = useCallback((url) => { navigate(url); setOpen(false); }, [navigate]);

  // ── Split pinned vs rest ──────────────────────────────────────────────────
  const pinned = notifications.filter((n) => n.is_pinned === 1 && !n.is_read);
  const rest = notifications.filter((n) => !(n.is_pinned === 1 && !n.is_read));
  const hasAny = notifications.length > 0;

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      {/* Bell button */}
      <Tooltip title="Notifications">
        <IconButton
          color="secondary"
          variant="light"
          ref={anchorRef}
          onClick={handleToggle}
          aria-controls={open ? "notif-popper" : undefined}
          aria-haspopup="true"
          sx={{
            color: "text.primary",
            bgcolor: open ? "grey.100" : "transparent",
            position: "relative",
          }}
        >
          <Badge badgeContent={unread_count || null} color="error" max={99}>
            <BellOutlined />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Popper panel */}
      <Popper
        id="notif-popper"
        placement={downMD ? "bottom" : "bottom-end"}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [{ name: "offset", options: { offset: [downMD ? -5 : 0, 9] } }],
        }}
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Transitions
            type="grow"
            position={downMD ? "top" : "top-right"}
            in={open}
            {...TransitionProps}
          >
            <Paper
              sx={{
                boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
                width: "100%",
                minWidth: 340,
                maxWidth: { xs: 340, md: 420 },
                borderRadius: "10px",
                overflow: "hidden",
                border: "1px solid #e8e8e8",
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Box>
                  {/* ── Header ── */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderBottom: "1px solid #f0f0f0",
                      bgcolor: "#fff",
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <BellOutlined style={{ fontSize: 16, color: "#1890ff" }} />
                      <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1a1a1a" }}>
                        Notifications
                      </Typography>
                      {unread_count > 0 && (
                        <Chip
                          label={`${unread_count} new`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            bgcolor: "#fff1f0",
                            color: "#cf1322",
                            border: "1px solid #ffa39e",
                            "& .MuiChip-label": { px: 0.8 },
                          }}
                        />
                      )}
                      {loading && <CircularProgress size={12} />}
                    </Stack>

                    {unread_count > 0 && (
                      <Tooltip title="Mark all as read">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={handleAllRead}
                          sx={{ p: 0.5 }}
                        >
                          <CheckCircleOutlined style={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {/* ── Body ── */}
                  <Box
                    sx={{
                      maxHeight: 480,
                      overflowY: "auto",
                      bgcolor: "#fff",
                      "&::-webkit-scrollbar": { width: 4 },
                      "&::-webkit-scrollbar-track": { bgcolor: "#fafafa" },
                      "&::-webkit-scrollbar-thumb": { bgcolor: "#d9d9d9", borderRadius: 4 },
                    }}
                  >
                    {!hasAny && !loading && (
                      <Box sx={{ py: 6, textAlign: "center" }}>
                        <CheckCircleOutlined style={{ fontSize: 32, color: "#b7eb8f" }} />
                        <Typography sx={{ mt: 1, color: "#8c8c8c", fontSize: 13 }}>
                          All caught up!
                        </Typography>
                        <Typography sx={{ color: "#bfbfbf", fontSize: 12 }}>
                          No pending notifications
                        </Typography>
                      </Box>
                    )}

                    {/* Pinned section */}
                    {pinned.length > 0 && (
                      <>
                        <SectionLabel label="📌 Pinned — Action Required" count={pinned.length} />
                        {pinned.map((n) => (
                          <NotifRow
                            key={n.id}
                            notif={n}
                            onRead={handleRead}
                            onNavigate={handleNavigate}
                          />
                        ))}
                        {rest.length > 0 && <Divider sx={{ my: 0, borderColor: "#f0f0f0" }} />}
                      </>
                    )}

                    {/* Rest */}
                    {rest.length > 0 && (
                      <>
                        {pinned.length > 0 && (
                          <SectionLabel label="Earlier" count={rest.filter((n) => !n.is_read).length} />
                        )}
                        {rest.map((n) => (
                          <NotifRow
                            key={n.id}
                            notif={n}
                            onRead={handleRead}
                            onNavigate={handleNavigate}
                          />
                        ))}
                      </>
                    )}
                  </Box>

                  {/* ── Footer ── */}
                  {hasAny && (
                    <Box
                      sx={{
                        px: 2,
                        py: 1.2,
                        borderTop: "1px solid #f0f0f0",
                        bgcolor: "#fafafa",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography sx={{ fontSize: 11, color: "#8c8c8c" }}>
                        Showing {notifications.length} notification{notifications.length !== 1 ? "s" : ""}
                      </Typography>
                      <Typography
                        component="span"
                        onClick={() => { navigate("/notifications"); setOpen(false); }}
                        sx={{
                          fontSize: 11,
                          color: "#1890ff",
                          cursor: "pointer",
                          fontWeight: 600,
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        View all →
                      </Typography>
                    </Box>
                  )}
                </Box>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}